import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { UTILS } from "utils";
import {
  useUpdateBrandNameMutation,
  useUpdateDomainNameMutation,
  useGetSettingsMutation,
} from "redux/settings/settingsSlice";
import AppModal from "components/ui/AppModal";
import AppInput from "components/ui/AppInput";
import AppButton from "components/ui/AppButton";
import AppIcon from "components/ui/AppIcon";

const GeneralSettings = () => {
  const { t } = useTranslation();
  const [orderPageLink, setOrderPageLink] = useState();
  const [Error, setError] = useState();
  const [open, setOpen] = useState(false);
  const { settings } = useSelector((state) => state?.SettingsReducer);
  const { isLoading } = useSelector((state) => state.LoaderReducer);
  const [getSettings] = useGetSettingsMutation();
  const [updateBrandName] = useUpdateBrandNameMutation();
  const { isError, domainError, brandError } = useSelector(
    (state) => state?.SettingErrorReducer
  );
  const [updateDomainName, { reset }] = useUpdateDomainNameMutation();
  const [state, setState] = useState({
    brandName: "",
    domainName: "",
  });

  const updateState = (e) => {
    const { name, value } = e.target;
    setState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    if (!value) {
      setError(null);
    }
  };

  const updateMyBrand = async () => {
    const userDetail = UTILS.getCurrentUser();
    const obj = {
      brandId: userDetail.brandId,
      brandName: state.brandName,
    };
    const { data } = await updateBrandName(obj);

    if (data?.success === 1) {
      await getSettings(obj);
      UTILS.saveCurrentUser({
        ...userDetail,
        brandName: state.brandName,
      });
      setState((prevState) => ({
        ...prevState,
        brandName: "",
      }));
    }
  };

  const updateMyDomain = () => {
    setOpen(true);
  };

  useEffect(() => {
    const userDetail = UTILS.getCurrentUser();
    const text = userDetail.storeFrontURL + userDetail?.temporaryDomainName;
    setOrderPageLink(text);
  }, [state.domainName]);

  const pressYes = async () => {
    reset();
    setError(null);
    const isValid = UTILS.isDomainNameValid(state.domainName);
    if (!isValid) {
      setError(t("Please enter a valid domain name"));
      setOpen(false);
      return;
    }
    const userDetail = UTILS.getCurrentUser();
    const obj = {
      brandId: userDetail.brandId,
      temporaryDomainName: state.domainName,
    };
    const { data } = await updateDomainName(obj);
    if (data?.success === 1) {
      await getSettings(obj);
      UTILS.saveCurrentUser({
        ...userDetail,
        temporaryDomainName: state.domainName,
      });

      setState((prevState) => ({
        ...prevState,
        domainName: "",
      }));
    }
    setOpen(false);
  };

  const pressNo = () => {
    setOpen(false);
  };

  const domainModalFooter = (
    <div className="flex flex-col-reverse gap-3 pt-4 sm:flex-row sm:justify-end">
      <AppButton variant="outline" size="lg" onClick={pressNo} disabled={isLoading}>
        {t("Cancel")}
      </AppButton>
      <AppButton
        variant="primary"
        size="lg"
        onClick={pressYes}
        loading={isLoading}
        disabled={isLoading}
      >
        {t("Update domain name")}
      </AppButton>
    </div>
  );

  return (
    <div className="w-full md:w-1/2 ">
      <div className="pb-2 w-full">
        {(isError || brandError) && (
          <div className="text-red-600">{brandError}</div>
        )}
        <div className="flex justify-between">
          <label className="text-gray-600 font-bold text-14px">
            {t("Business name")}
          </label>
        </div>
        <div className="flex justify-between gap-3 items-center">
          <AppInput
            placeholder={settings?.browserTabTitle}
            compact
            name="brandName"
            value={state?.brandName}
            className="py-2 rounded"
            onInput={(e) => {
              updateState(e);
            }}
          />
          <AppButton disabled={!state?.brandName} onClick={updateMyBrand}>
            {t("Update")}
          </AppButton>
        </div>
      </div>

      <div className="pb-2 w-full mt-4">
        {(isError || Error || domainError) && (
          <div className="text-red-600 py-3">{domainError || Error}</div>
        )}

        <AppModal
          open={open}
          onClose={pressNo}
          width={560}
          footer={domainModalFooter}
          bodyStyle={{ paddingTop: 8 }}
          closable={false}
        >
          <div className="flex flex-col gap-4 text-left">
            <h2 className="m-0 text-center text-20px font-semibold text-gray-900">
              {t("Update domain name")}
            </h2>
            <div className="space-y-3 rounded-2xl bg-amber-50/60 px-4 py-3">
              <p className="m-0 text-sm leading-relaxed text-gray-800">
                {t("If you change your domain name")}
              </p>
              <p className="m-0 text-sm leading-relaxed text-gray-700">
                {t("Do not change the domain name")}
              </p>
            </div>
          </div>
        </AppModal>

        <div className="flex justify-between">
          <label className="text-gray-600 font-bold text-14px">
            {t("Domain name")}
          </label>
        </div>
        <div className="flex justify-between gap-3 items-center">
          <AppInput
            placeholder={settings?.temporaryDomainName}
            name="domainName"
            compact
            value={state?.domainName}
            className="py-2 rounded"
            onInput={(e) => {
              updateState(e);
            }}
          />
          <AppButton disabled={!state?.domainName} onClick={updateMyDomain}>
            {t("Update")}
          </AppButton>
        </div>
        <div className="text-gray-400 text-12px mt-1">
          <span className="text-red-500 font-semibold">{t("Note:")}</span>
          <span>
            {t("Lower case alphabets, numeric and '-' are allowed only")}
          </span>
        </div>
      </div>
      <div className="pb-2 w-full mt-4">
        <div className="flex justify-between">
          <label className="text-gray-600 font-bold text-14px">
            {t("Ordering page link")}
          </label>
        </div>
        <div className="flex justify-between items-center">
          <AppInput
            disabled
            compact
            placeholder={orderPageLink}
            value=""
            className="py-2 rounded"
          >
            <span
              className="cursor-pointer"
              onClick={() => {
                navigator.clipboard.writeText(orderPageLink);
                UTILS.successNotification(t("Copied to Clipboard"));
              }}
            >
              <AppIcon name="copy" />{" "}
            </span>
          </AppInput>
        </div>
      </div>
    </div>
  );
};

export default GeneralSettings;
